import { useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { Button } from '../../../components/button';
import BottomContainer from '../../../components/containers/BottomContainer';
import text from '../../../text';
import useRedirectToMap from '../../hooks/useRedirectToMap';

export interface StepFooterButtonProps extends React.ComponentProps<'button'> {
  label?: string | React.ReactNode;
  loading?: boolean;
}

type StepFooterProps = {
  previousButtonProps?: StepFooterButtonProps;
  nextButtonProps?: StepFooterButtonProps;
  topContent?: React.ReactNode;
  onCancel: () => void;
  skipAutoNextStep?: boolean;
};

const StepFooterInner = ({
  previousButtonProps = {},
  nextButtonProps = {},
  topContent,
  onCancel,
  skipAutoNextStep = false,
}: StepFooterProps) => {
  const { previousStep, nextStep, isFirstStep, activeStep } = useWizard();
  const { map2dId, redirectToMap } = useRedirectToMap();

  const handlePreviousStep = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      previousStep();
      previousButtonProps?.onClick?.(event);

      // if queryParam mp is present, redirect to map
      // this happens when customer clicks Confirm & Pay from email
      // step 1 is Membership or Project Details(if Essential)
      if (map2dId && activeStep === 1) {
        redirectToMap();
      }
    },
    [previousStep, previousButtonProps, map2dId, activeStep, redirectToMap]
  );

  return (
    <BottomContainer className='StepFooter'>
      <div className='top'>{topContent}</div>
      <div className='bottom'>
        <div className='left'>
          <Button variant='link' onClick={onCancel}>
            {text('cancel')}
          </Button>
        </div>
        <div className='right'>
          {!isFirstStep && (
            <Button
              variant='outline-primary'
              {...previousButtonProps}
              onClick={handlePreviousStep}
            >
              {previousButtonProps.label || text('previous')}
            </Button>
          )}
          <Button
            className='ml-3 full-width'
            {...nextButtonProps}
            onClick={(event) => {
              if (!skipAutoNextStep) {
                nextStep();
              }
              nextButtonProps?.onClick?.(event);
            }}
          >
            {nextButtonProps.label || text('next')}
          </Button>
        </div>
      </div>
    </BottomContainer>
  );
};

const StepFooterWithSubmit = ({
  nextButtonProps,
  ...props
}: StepFooterProps) => {
  const { submitForm, isSubmitting } = useFormikContext();
  const { nextStep } = useWizard();

  return (
    <StepFooterInner
      skipAutoNextStep={true}
      nextButtonProps={{
        ...(nextButtonProps ?? {}),
        loading: isSubmitting,
        onClick: async (event) => {
          if (!isSubmitting) {
            await submitForm();
            nextStep();
          }
          nextButtonProps?.onClick?.(event);
        },
      }}
      {...props}
    />
  );
};

const StepFooter = ({
  submit,
  ...props
}: StepFooterProps & { submit?: boolean }) => {
  return submit ? (
    <StepFooterWithSubmit {...props} />
  ) : (
    <StepFooterInner {...props} />
  );
};

export default StepFooter;
